/** Shown while metrics need more message history. */
export const METRICS_CALCULATING = 'Calculating…';

/** Shown when a value cannot be derived. */
export const METRICS_UNAVAILABLE = '—';

/**
 * @param {unknown} ts Firestore Timestamp, Date, or ISO string.
 * @returns {number | null}
 */
export function toTimestampMillis(ts) {
  if (!ts) return null;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/**
 * @param {number | null | undefined} ms
 * @returns {string}
 */
export function formatDurationMs(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return METRICS_UNAVAILABLE;
  const totalSec = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * @param {number | null | undefined} ms
 * @returns {string}
 */
export function formatLatencyMs(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return METRICS_UNAVAILABLE;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return formatDurationMs(ms);
}

/**
 * @param {Record<string, unknown> | null | undefined} session
 * @param {string[]} keys
 * @returns {string}
 */
export function pickSessionString(session, keys) {
  if (!session || typeof session !== 'object') return '';
  for (const key of keys) {
    const value = session[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

/**
 * @param {{ senderType?: string, sender?: string, isInternal?: boolean }} msg
 * @returns {boolean}
 */
export function isVisitorMessage(msg) {
  if (!msg || msg.isInternal === true) return false;
  const sender = msg.senderType || msg.sender;
  return sender !== 'agent';
}

/**
 * @param {{ senderType?: string, sender?: string, isInternal?: boolean }} msg
 * @returns {boolean}
 */
export function isAgentMessage(msg) {
  if (!msg || msg.isInternal === true) return false;
  const sender = msg.senderType || msg.sender;
  return sender === 'agent';
}

/**
 * @param {Array<object>} messages
 * @returns {Array<object>}
 */
export function sortMessagesChronologically(messages) {
  return [...(Array.isArray(messages) ? messages : [])].sort((a, b) => {
    const ta = toTimestampMillis(a.timestamp) ?? 0;
    const tb = toTimestampMillis(b.timestamp) ?? 0;
    return ta - tb;
  });
}

/**
 * @param {string} timezone IANA timezone id.
 * @returns {string}
 */
export function formatUserLocalTime(timezone) {
  if (!timezone) return METRICS_UNAVAILABLE;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZoneName: 'short',
    }).format(new Date());
  } catch {
    return 'Invalid timezone';
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} session
 * @returns {string}
 */
export function resolveOriginatingNode(session) {
  const url = pickSessionString(session, [
    'pageUrl',
    'originUrl',
    'originPageUrl',
    'sourceUrl',
    'referrerUrl',
  ]);
  return url || METRICS_UNAVAILABLE;
}

/**
 * @param {Record<string, unknown> | null | undefined} session
 * @returns {string}
 */
export function resolveGeographicConsistency(session) {
  const ipLocation = pickSessionString(session, [
    'ipLocation',
    'ipDerivedLocation',
    'geoFromIp',
    'visitorIpLocation',
  ]);
  const profileLocation = pickSessionString(session, [
    'visitorLocation',
    'profileLocation',
    'location',
    'visitorProfileLocation',
  ]);

  if (!ipLocation && !profileLocation) return METRICS_UNAVAILABLE;
  if (!ipLocation || !profileLocation) return 'Insufficient data';

  const normalize = (value) => value.toLowerCase().replace(/\s+/g, ' ').trim();
  return normalize(ipLocation) === normalize(profileLocation) ? 'Match' : 'IP/Profile Mismatch';
}

/**
 * @param {Array<object>} messages
 * @param {Record<string, unknown> | null | undefined} session
 * @param {{ loading?: boolean }} [options]
 * @returns {Record<string, string>}
 */
export function computeChatSessionMetrics(messages, session, options = {}) {
  const { loading = false } = options;
  const sessionReference =
    typeof session?.id === 'string' && session.id ? session.id : METRICS_UNAVAILABLE;

  const sorted = sortMessagesChronologically(
    (Array.isArray(messages) ? messages : []).filter((m) => m?.isInternal !== true),
  );

  const visitorTimezone = pickSessionString(session, [
    'visitorTimezone',
    'timezone',
    'visitorTimeZone',
  ]);
  const userLocalTime = formatUserLocalTime(visitorTimezone);
  const originatingNode = resolveOriginatingNode(session);
  const geographicConsistency = resolveGeographicConsistency(session);

  if (loading) {
    return {
      sessionReference,
      activeEngagementTime: METRICS_CALCULATING,
      initialTtr: METRICS_CALCULATING,
      meanAgentLatency: METRICS_CALCULATING,
      peakWaitTime: METRICS_CALCULATING,
      engagementFrequency: METRICS_CALCULATING,
      originatingNode,
      geographicConsistency,
      userLocalTime: visitorTimezone ? userLocalTime : METRICS_CALCULATING,
    };
  }

  if (sorted.length === 0) {
    return {
      sessionReference,
      activeEngagementTime: METRICS_UNAVAILABLE,
      initialTtr: METRICS_UNAVAILABLE,
      meanAgentLatency: METRICS_UNAVAILABLE,
      peakWaitTime: METRICS_UNAVAILABLE,
      engagementFrequency: '0',
      originatingNode,
      geographicConsistency,
      userLocalTime,
    };
  }

  if (sorted.length < 2) {
    const visitorCount = sorted.filter(isVisitorMessage).length;
    return {
      sessionReference,
      activeEngagementTime: METRICS_CALCULATING,
      initialTtr: METRICS_CALCULATING,
      meanAgentLatency: METRICS_CALCULATING,
      peakWaitTime: METRICS_CALCULATING,
      engagementFrequency: String(visitorCount),
      originatingNode,
      geographicConsistency,
      userLocalTime,
    };
  }

  const firstTs = toTimestampMillis(sorted[0].timestamp);
  const lastTs = toTimestampMillis(sorted[sorted.length - 1].timestamp);
  const activeEngagementTime =
    firstTs != null && lastTs != null
      ? formatDurationMs(lastTs - firstTs)
      : METRICS_UNAVAILABLE;

  const firstVisitor = sorted.find(isVisitorMessage);
  const firstAgent = sorted.find(isAgentMessage);
  let initialTtr = METRICS_UNAVAILABLE;
  if (firstVisitor && firstAgent) {
    const visitorTs = toTimestampMillis(firstVisitor.timestamp);
    const agentTs = toTimestampMillis(firstAgent.timestamp);
    if (visitorTs != null && agentTs != null && agentTs >= visitorTs) {
      initialTtr = formatLatencyMs(agentTs - visitorTs);
    } else if (sorted.length < 2) {
      initialTtr = METRICS_CALCULATING;
    }
  } else {
    initialTtr = METRICS_CALCULATING;
  }

  const waitGaps = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (!isVisitorMessage(sorted[i])) continue;
    const visitorTs = toTimestampMillis(sorted[i].timestamp);
    if (visitorTs == null) continue;

    const nextAgent = sorted.slice(i + 1).find(isAgentMessage);
    if (!nextAgent) continue;

    const agentTs = toTimestampMillis(nextAgent.timestamp);
    if (agentTs == null || agentTs < visitorTs) continue;
    waitGaps.push(agentTs - visitorTs);
  }

  const meanAgentLatency =
    waitGaps.length > 0
      ? formatLatencyMs(waitGaps.reduce((sum, gap) => sum + gap, 0) / waitGaps.length)
      : METRICS_CALCULATING;

  const peakWaitTime =
    waitGaps.length > 0
      ? formatLatencyMs(Math.max(...waitGaps))
      : METRICS_CALCULATING;

  const visitorMessageCount = sorted.filter(isVisitorMessage).length;
  let engagementFrequency = String(visitorMessageCount);
  if (firstTs != null && lastTs != null && lastTs > firstTs) {
    const durationMinutes = (lastTs - firstTs) / 60000;
    if (durationMinutes > 0) {
      engagementFrequency = `${(visitorMessageCount / durationMinutes).toFixed(1)}/min`;
    }
  }

  return {
    sessionReference,
    activeEngagementTime,
    initialTtr,
    meanAgentLatency,
    peakWaitTime,
    engagementFrequency,
    originatingNode,
    geographicConsistency,
    userLocalTime,
  };
}

/**
 * @param {string | undefined} status
 * @returns {{ label: string, color: 'success' | 'default' | 'warning' }}
 */
export function visitorPresenceFromStatus(status) {
  const normalized = typeof status === 'string' ? status.trim().toLowerCase() : '';
  if (['open', 'active', 'awaiting_customer', 'pending'].includes(normalized)) {
    return { label: 'Online', color: 'success' };
  }
  if (['closed', 'resolved'].includes(normalized)) {
    return { label: 'Offline', color: 'default' };
  }
  return { label: 'Unknown', color: 'warning' };
}
